import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Panel } from '@tanium/react-panel';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { RibbonColumn, StackedRibbonList } from '@tanium/react-stacked-ribbon-list';
import { EditIcon, TrashIcon, AddIcon } from '@tanium/react-graphics';
import { Link } from '@tanium/react-link';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import Helper from 'utils/Helper';
import ConfirmationModal from 'components/pages/request/requestPartials/ConfirmationModal';
import { AwsRoleTargetAccess } from 'gql/AwsRoleTargetAccess';
import InlineCopyToClipboard from 'components/common/copyToClipboard/inlineCopyToClipboard';
import AwsRoleTargetAccessSelectors from 'components/pages/console/store/AwsRoleTargetAccessSelectors';
import AwsRoleTargetAccessSlice from 'components/pages/console/store/AwsRoleTargetAccessSlice';
import { TARGET_GROUP_ROUTE } from 'Route';
import AwsRoleTargetAccessCRUDModal, { Operation } from './awsRoleTargetAccessCRUDModal';

type AwsRoleTargetAccessProps = {
  targetOrGroupID: string;
  allowEditing: boolean;
};

const AwsRoleTargetAccessList: React.VFC<AwsRoleTargetAccessProps> = ({ targetOrGroupID, allowEditing }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loading = useSelector(AwsRoleTargetAccessSelectors.selectAwsRoleTargetAccessLoading);
  const mutationSucceeded = useSelector(AwsRoleTargetAccessSelectors.selectMutationSucceeded);
  const awsRoleAccess = useSelector(AwsRoleTargetAccessSelectors.selectAwsRoleTargetAccesses);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedAwsRole, setSelectedAwsTargetRole] = useState<AwsRoleTargetAccess | undefined>(undefined);
  const columns: RibbonColumn<AwsRoleTargetAccess>[] = useMemo(
    () => [
      {
        label: t('ACTIONS'),
        dataKey: 'id',
        grow: 0,
        width: 50,
        isSortDisabled: true,
        renderer: (data) => {
          return allowEditing && data.itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <div>
              <TooltippedIconButton
                data-testid={`awsRoleTargetAccess-edit-${data.index}`}
                tooltipContent={t('TARGET_ACCESS_EDIT')}
                label={t('TARGET_ACCESS_EDIT')}
                height={16}
                width={16}
                icon={EditIcon}
                onClick={() => {
                  setSelectedAwsTargetRole(data.itemData);
                  setIsOpen(true);
                }}
              />
              <TooltippedIconButton
                data-testid={`awsRoleTargetAccess-delete-${data.index}`}
                style={{ marginLeft: '0.5em' }}
                tooltipContent={t('TARGET_ACCESS_DELETE')}
                label={t('TARGET_ACCESS_DELETE')}
                height={16}
                width={16}
                icon={TrashIcon}
                onClick={() => {
                  setSelectedAwsTargetRole(data.itemData);
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            </div>
          ) : (
            <></>
          );
        },
      },
      {
        label: t('AWS_ROLE_TARGET_ACCESS_ROLE_NAME_LABEL'),
        dataKey: 'awsRoleName',
        primary: true,
      },
      {
        label: t('ID'),
        dataKey: 'aws-role-target-access-id',
        renderer: ({ itemData }) => {
          return (
            <div>
              <InlineCopyToClipboard
                displayedText={itemData.id}
                copyText={itemData.id}
                iconSize={{ width: 16, height: 16 }}
                tooltipContent={t('COPY_ID')}
              />
            </div>
          );
        },
      },
      {
        label: t('DESCRIPTION'),
        dataKey: 'description',
      },
      {
        label: t('AD_GROUPS'),
        dataKey: 'adGroups',
        renderer: ({ itemData }) => {
          return itemData.adGroups.join(', ');
        },
      },
      {
        label: t('PARENT_ID'),
        dataKey: 'targetOrTargetGroupID',
        renderer: ({ itemData }) => {
          return itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <>{t('SELF')}</>
          ) : (
            <Link
              data-testid={`link-${itemData.id}`}
              href={Helper.interpolateRoute(TARGET_GROUP_ROUTE.url, { targetGroupId: itemData.targetOrTargetGroupID })}
            >
              {itemData.targetOrTargetGroupID}
            </Link>
          );
        },
      },
    ],
    [setIsDeleteConfirmationOpen, setSelectedAwsTargetRole, setIsOpen, targetOrGroupID, t, allowEditing]
  );

  const createAwsRoleTargetAccess = useCallback(() => {
    setSelectedAwsTargetRole(undefined);
    setIsOpen(true);
  }, [setIsOpen]);

  const dataGetter = useCallback(
    (index: number): AwsRoleTargetAccess => {
      return awsRoleAccess[index];
    },
    [awsRoleAccess]
  );

  const itemIdGetter = useCallback(
    (index: number) => {
      return dataGetter(index).id;
    },
    [dataGetter]
  );

  const isItemLoaded = useCallback(
    (index: number) => {
      return !!dataGetter(index);
    },
    [dataGetter]
  );

  const isIdEmphasized = useCallback(
    (id: string) => {
      const item = awsRoleAccess.find((role) => role.id === id);
      return item?.targetOrTargetGroupID === targetOrGroupID;
    },
    [awsRoleAccess, targetOrGroupID]
  );

  const handleDelete = useCallback(
    (proceed: boolean) => {
      if (proceed && selectedAwsRole !== undefined) {
        dispatch(AwsRoleTargetAccessSlice.actions.deleteAwsRoleTargetAccess(selectedAwsRole.id));
      }

      setIsDeleteConfirmationOpen(false);
      setSelectedAwsTargetRole(undefined);
    },
    [setIsDeleteConfirmationOpen, setSelectedAwsTargetRole, selectedAwsRole, dispatch]
  );

  const handleCreateOrUpdate = useCallback(
    (operation: Operation) => {
      setIsOpen(false);

      if (operation.type === 'cancel') {
        return;
      }

      switch (operation.type) {
        case 'create':
          dispatch(
            AwsRoleTargetAccessSlice.actions.createAwsRoleTargetAccess({
              adGroups: operation.adGroups,
              awsRoleName: operation.awsRoleName,
              awsSessionPolicy: operation.awsSessionPolicy,
              targetOrTargetGroupID: operation.targetOrTargetGroupId,
              description: operation.description,
            })
          );
          break;
        case 'update':
          dispatch(
            AwsRoleTargetAccessSlice.actions.updateAwsRoleTargetAccess({
              adGroups: operation.adGroups,
              awsRoleName: operation.awsRoleName,
              awsSessionPolicy: operation.awsSessionPolicy,
              targetOrTargetGroupID: operation.targetOrTargetGroupId,
              description: operation.description,
              id: operation.id,
            })
          );
          break;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (mutationSucceeded) {
      dispatch(
        AwsRoleTargetAccessSlice.actions.listAwsRoleTargetAccessForTargetOrGroup({
          includeAllTargetGroups: true,
          targetOrTargetGroupID: targetOrGroupID,
        })
      );
    }
  }, [dispatch, targetOrGroupID, mutationSucceeded]);

  const listActions = allowEditing ? (
    <Stack>
      <Button data-testid="awsRoleTargetAccess-create" size="sm" onClick={createAwsRoleTargetAccess}>
        <Box pr={1} display={'inline-block'}>
          <AddIcon height={'10px'} />
        </Box>
        {t('CREATE')}
      </Button>
    </Stack>
  ) : (
    <></>
  );

  return (
    <Panel title={t('AWS_ROLE_TARGET_ACCESS_TITLE')}>
      <Stack direction="column" itemSpacing={1} data-testid="awsRoleTargetAccess">
        <StackedRibbonList
          enableRowHighlight={false}
          columns={columns}
          isItemLoaded={isItemLoaded}
          height={awsRoleAccess.length > 0 ? '12em' : '7.4em'}
          dataGetter={dataGetter}
          itemCount={awsRoleAccess.length}
          itemIdGetter={itemIdGetter}
          listActions={listActions}
          isIdEmphasized={isIdEmphasized}
          isLoading={loading}
        />
        <AwsRoleTargetAccessCRUDModal
          isOpen={isOpen}
          awsRoleToEdit={selectedAwsRole}
          onProcess={handleCreateOrUpdate}
          targetOrTargetGroupId={targetOrGroupID}
        />
        <ConfirmationModal
          timeoutSeconds={1}
          headerText={t('AWS_ROLE_TARGET_ACCESS_DELETE_HEADER')}
          confirmationText={t('DELETE')}
          closeModal={handleDelete}
          isOpen={isDeleteConfirmationOpen}
        >
          <>{t('AWS_ROLE_TARGET_ACCESS_DELETE_CONTENT')}</>
        </ConfirmationModal>
      </Stack>
    </Panel>
  );
};

export default AwsRoleTargetAccessList;
